import { OCEAN_FCL_CATALOG } from "./catalogs/oceanFcl";

const LEGACY_TMS =
    process.env.REACT_APP_LEGACY_TMS_URL || "https://tmsapp.infoxsystem.com/tms";

/**
 * @typedef {object} ReportModeConfig
 * @property {string} reportsPath - Route to the report manager hub for this mode
 * @property {string} breadcrumb
 * @property {string} title
 * @property {string} subtitle
 * @property {string} listPath - Back link to module list page
 * @property {string} listLabel
 */

/**
 * @typedef {object} ReportModuleConfig
 * @property {string} id
 * @property {string} label
 * @property {Array<object>} catalog
 * @property {Record<string, ReportModeConfig>} modes
 * @property {(mode: string, legacyPage: string) => string | null} [resolveLegacyHref]
 */

/** @type {Record<string, ReportModuleConfig>} */
const REPORT_MODULES = {
    "ocean-fcl": {
        id: "ocean-fcl",
        label: "Ocean FCL",
        catalog: OCEAN_FCL_CATALOG,
        modes: {
            export: {
                reportsPath: "/ocean/export/reports",
                breadcrumb: "OCEAN FREIGHT • EXPORT",
                title: "Reports",
                subtitle:
                    "AI-powered Ocean Export reports — grouped by team: Ops • Sales • Accounting • Others.",
                listPath: "/booking",
                listLabel: "Shipment List",
            },
            import: {
                reportsPath: "/ocean/import/reports",
                breadcrumb: "OCEAN FREIGHT • IMPORT",
                title: "Reports",
                subtitle:
                    "AI-powered Ocean Import reports — grouped by team: Ops • Sales • Accounting • Others.",
                listPath: "/import-booking",
                listLabel: "Import Shipment",
            },
        },
        resolveLegacyHref: (mode, legacyPage) => {
            if (!legacyPage) return null;
            const segment = mode === "import" ? "Import" : "Export";
            return `${LEGACY_TMS}/FCL/${segment}/${legacyPage}`;
        },
    },
};

/**
 * Register an additional report module at runtime (e.g. from a feature plugin).
 * @param {ReportModuleConfig} config
 */
export function registerReportModule(config) {
    if (!config?.id) {
        throw new Error("registerReportModule: config.id is required");
    }
    REPORT_MODULES[config.id] = config;
}

/**
 * @param {string} moduleId
 * @returns {ReportModuleConfig | undefined}
 */
export function getReportModule(moduleId) {
    return REPORT_MODULES[moduleId];
}

/**
 * @param {string} moduleId
 * @param {string} mode
 * @returns {ReportModeConfig | undefined}
 */
export function getReportModeConfig(moduleId, mode) {
    const mod = getReportModule(moduleId);
    if (!mod?.modes) return undefined;
    return mod.modes[mode] ?? mod.modes[mod.defaultMode] ?? Object.values(mod.modes)[0];
}

/**
 * @param {string} moduleId
 * @param {string} mode
 */
export function getReportsPath(moduleId, mode) {
    return getReportModeConfig(moduleId, mode)?.reportsPath ?? "/";
}

export { REPORT_MODULES };
